import {
  type ActionIconProps,
  Box,
  Group,
  type GroupProps,
  type MantineStyleProp,
  Paper,
  type PaperProps,
  Skeleton,
  Tooltip,
} from "@mantine/core"
import { modals } from "@mantine/modals"
import type { PropsWithChildren } from "react"
import { EditIcon } from "@/components/forms"
import { AppTitle } from "@/components/utils"
import { useBreakpoints } from "@/hooks"

type ModalSettings = Parameters<typeof modals.open>[0]

export function EditButton({
  title,
  children,
  modalProps,
  ...props
}: ActionIconProps & { title: string; modalProps?: Omit<ModalSettings, "children" | "title"> }) {
  return (
    <EditIcon
      variant="subtle"
      {...props}
      onClick={(e) => {
        e.stopPropagation()
        return modals.open({
          ...modalProps,
          title: <AppTitle>{title as string}</AppTitle>,
          children,
        })
      }}
    />
  )
}

export function ProfileCardRow({
  children,
  icon,
  label,
  active,
  ...props
}: {
  icon: React.ReactNode
  label?: string
  active?: boolean
} & GroupProps) {
  return (
    <Tooltip label={label} disabled={!label} withArrow position="bottom-start">
      <Group
        gap="xs"
        wrap="nowrap"
        {...props}
        style={{
          color: active ? "var(--mantine-color-red-5)" : "inherit",
          fontWeight: active ? 700 : "inherit",
        }}
      >
        {icon && (
          <Box component="span" style={{ height: 24, color: "var(--mantine-color-gray-5)" }}>
            {icon}
          </Box>
        )}
        {children}
      </Group>
    </Tooltip>
  )
}

export function Block({ children, ...props }: PropsWithChildren<PaperProps>) {
  return (
    <Paper px="md" py="sm" shadow="md" h="100%" pos="relative" {...props}>
      {children}
    </Paper>
  )
}

export function BlockSkeleton() {
  return <Skeleton mih={200} radius="lg" />
}

interface IProps {
  xs?: MantineStyleProp
  sm?: MantineStyleProp
  md?: MantineStyleProp
  lg?: MantineStyleProp
  xl?: MantineStyleProp
}

export function Panel({
  children,
  block,
  xs,
  sm,
  md,
  lg,
  xl,
  ...props
}: PropsWithChildren & { block?: boolean } & IProps & PaperProps) {
  const sx = useBreakpoints({ xs, sm, md, lg, xl })
  return <Box style={sx}>{block ? <Block {...props}>{children}</Block> : children}</Box>
}
